import { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Input, InputGroup, Loader, Modal } from 'rsuite';
import { FaPlus, FaSync, FaSearch, FaRegFileAlt, FaRegLightbulb } from 'react-icons/fa';
import { useAuth } from '../../../hooks/auth';
import { useCustomTheme } from '../../../hooks/useCustomTheme';
import { toastSuccess, toastError } from '../../../toasts';
import {
  listTemplates,
  syncTemplates,
  deleteTemplate,
  createDefaultTemplate,
  restoreTemplate,
} from '../../../services/uniservice/uniservice.service';
import TemplateCard from './TemplateCard';
import TemplateEditorModal from './TemplateEditorModal';
import { TEMPLATE_FILTERS, PROTECTED_TEMPLATE_NAME } from './templateConstants';
import { styles } from './styles';

// Tab "Templates Oficiais": templates HSM aprovados pela Meta (reengajamento
// fora da janela de 24h). Fonte: UniService (gateway oficial).
export default function OfficialTemplates({ showPreview }) {
  const { id_empresa } = useAuth();
  const { theme } = useCustomTheme();

  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [creatingDefault, setCreatingDefault] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [filter, setFilter] = useState('ALL');
  const [search, setSearch] = useState('');
  const [editor, setEditor] = useState({ open: false, template: null });
  const [toDelete, setToDelete] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setTemplates(await listTemplates(id_empresa, 'all', true));
    setLoading(false);
  }, [id_empresa]);

  useEffect(() => {
    load();
  }, [load]);

  const hasDefault = useMemo(
    () => templates.some((t) => t.name === PROTECTED_TEMPLATE_NAME && !t.hidden),
    [templates],
  );

  const counts = useMemo(() => {
    const by = { ALL: 0, APPROVED: 0, PENDING: 0, REJECTED: 0, HIDDEN: 0 };
    templates.forEach((t) => {
      if (t.hidden) {
        by.HIDDEN += 1;
        return;
      }
      by.ALL += 1;
      if (t.status === 'APPROVED') by.APPROVED += 1;
      else if (t.status === 'REJECTED') by.REJECTED += 1;
      else by.PENDING += 1;
    });
    return by;
  }, [templates]);

  const visible = useMemo(() => {
    const term = search.trim().toLowerCase();
    return templates.filter((t) => {
      if (filter === 'HIDDEN') {
        if (!t.hidden) return false;
      } else if (t.hidden) {
        return false;
      }
      const matchesFilter =
        filter === 'ALL' ||
        filter === 'HIDDEN' ||
        (filter === 'APPROVED' && t.status === 'APPROVED') ||
        (filter === 'REJECTED' && t.status === 'REJECTED') ||
        (filter === 'PENDING' && !['APPROVED', 'REJECTED'].includes(t.status));
      const matchesSearch =
        !term || (t.name || '').toLowerCase().includes(term) || (t.bodyText || '').toLowerCase().includes(term);
      return matchesFilter && matchesSearch;
    });
  }, [templates, filter, search]);

  const handleSync = async () => {
    setSyncing(true);
    try {
      const data = await syncTemplates(id_empresa);
      const result = data?.result || data || {};
      await load();
      toastSuccess(`Sincronizado: ${result.created || 0} novos, ${result.updated || 0} atualizados, ${result.deleted || 0} removidos.`);
    } catch {
      toastError('Não foi possível sincronizar com a Meta');
    } finally {
      setSyncing(false);
    }
  };

  const handleCreateDefault = async () => {
    setCreatingDefault(true);
    try {
      const res = await createDefaultTemplate(id_empresa);
      if (res?.ok) toastSuccess(res.message || 'Template padrão criado!');
      else toastError(res?.message || 'Não foi possível criar o template padrão.');
      await load();
    } catch {
      toastError('Não foi possível criar o template padrão.');
    } finally {
      setCreatingDefault(false);
    }
  };

  const handleRestore = async (template) => {
    try {
      await restoreTemplate(id_empresa, template.id);
      toastSuccess('Template restaurado');
      load();
    } catch {
      toastError('Não foi possível restaurar o template');
    }
  };

  const confirmDelete = async () => {
    if (!toDelete || toDelete.name === PROTECTED_TEMPLATE_NAME) {
      setToDelete(null);
      return;
    }
    try {
      const res = await deleteTemplate(id_empresa, toDelete.id);
      toastSuccess(res?.hidden ? 'Template removido da sua lista (a Meta não permite excluí-lo de fato).' : 'Template excluído');
      setToDelete(null);
      load();
    } catch {
      toastError('Não foi possível excluir o template');
    }
  };

  return (
    <div>
      <div style={styles.toolbar}>
        <div style={styles.toolbarInfo(theme)}>
          Aprovados pela Meta para reengajar o cliente fora da janela de 24h · <b style={{ color: theme.colors.text }}>{counts.ALL}</b> ativos
        </div>
        <div style={styles.toolbarActions}>
          <Button appearance="ghost" startIcon={syncing ? <Loader /> : <FaSync />} onClick={handleSync} disabled={syncing}>Sincronizar</Button>
          <Button appearance="primary" startIcon={<FaPlus />} onClick={() => setEditor({ open: true, template: null })} style={{ backgroundColor: theme.colors.primary }}>Novo template</Button>
        </div>
      </div>

      {!loading && !hasDefault && (
        <div style={styles.callout(theme)}>
          <div style={styles.calloutIcon(theme)}><FaRegLightbulb size={18} /></div>
          <div style={styles.calloutBody}>
            <div style={styles.calloutTitle(theme)}>Ative as notificações de pedido no WhatsApp</div>
            <div style={styles.calloutText(theme)}>
              Para usar a API oficial do WhatsApp no MenuCheff, você precisa criar o template padrão do nosso
              sistema. É ele que avisa o cliente sobre o pedido mesmo fora da janela de 24h.
            </div>
          </div>
          <Button appearance="primary" loading={creatingDefault} onClick={handleCreateDefault} style={{ backgroundColor: theme.colors.primary, whiteSpace: 'nowrap' }}>
            Criar template padrão
          </Button>
        </div>
      )}

      <div style={styles.filters}>
        <div style={styles.segbar(theme)}>
          {TEMPLATE_FILTERS.map((f) => (
            <button key={f.key} type="button" style={styles.seg(theme, filter === f.key)} onClick={() => setFilter(f.key)}>
              {f.label} ({counts[f.key] ?? 0})
            </button>
          ))}
        </div>
        <InputGroup inside style={styles.searchWrap}>
          <Input placeholder="Buscar template..." value={search} onChange={setSearch} />
          <InputGroup.Addon><FaSearch /></InputGroup.Addon>
        </InputGroup>
      </div>

      {loading ? (
        <Loader center size="md" content="Carregando templates..." />
      ) : visible.length === 0 ? (
        <div style={styles.emptyState}>
          <div style={styles.emptyIcon(theme)}><FaRegFileAlt size={26} /></div>
          <p style={styles.emptyText(theme)}>
            {templates.length === 0
              ? 'Nenhum template cadastrado. Crie o primeiro ou sincronize com a Meta.'
              : 'Nenhum template encontrado para este filtro.'}
          </p>
        </div>
      ) : (
        <div style={styles.grid}>
          {visible.map((template) => (
            <TemplateCard
              key={template.id || template.name}
              template={template}
              showPreview={showPreview}
              onEdit={() => setEditor({ open: true, template })}
              onDelete={() => setToDelete(template)}
              onRestore={() => handleRestore(template)}
            />
          ))}
        </div>
      )}

      <TemplateEditorModal
        open={editor.open}
        template={editor.template}
        companyId={id_empresa}
        onClose={() => setEditor({ open: false, template: null })}
        onSaved={load}
      />

      <Modal open={Boolean(toDelete)} onClose={() => setToDelete(null)} size="xs">
        <Modal.Header><Modal.Title>Excluir template</Modal.Title></Modal.Header>
        <Modal.Body>Excluir <strong>{toDelete?.name}</strong>? Isso remove o template na Meta também.</Modal.Body>
        <Modal.Footer>
          <Button appearance="subtle" onClick={() => setToDelete(null)}>Cancelar</Button>
          <Button appearance="primary" color="red" onClick={confirmDelete}>Excluir</Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}
